import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Offcanvas,
  Button,
  Form,
  FormControl,
  Row,
  Col,
  InputGroup,
  Spinner,
  Alert,
} from 'react-bootstrap';
import {
  Search as FiSearch,
  Trash3 as FiTrash2,
  X as FiX,
  Plus as FiPlus,
  Image as FiImage,
} from 'react-bootstrap-icons';
import {
  Client,
  loadClients,
  searchSalesPeople,
  SalesPerson,
} from '../../api/clients';
import { loadProducts } from '../../api/products';
import { Product } from '../../models/product';
import { loadConfigurationFactors } from '../../api/configuration';
import { listExchangeSourcesForPair } from '../../api/exchangeSources';
import { getCurrentAuthUser } from '../../utils/currentAuthUser';
import { formatExchangeRate } from '../../utils/formatExchangeRate';
import { useLoading } from '../../hooks/useLoading';
import { SUPPORTED_CURRENCIES } from '../../models/organization';
import type { ExchangeSourceOption } from '../../models/exchangeSource';
import { Quote } from '../../models/quote';
import {
  createQuoteServer,
  updateQuoteServer,
} from '../../api/quotationsServer';
import type { OrderFormLine, OrderFormTaxConfig } from '../orders/orderForm/types';
import { computeOrderTotals } from '../orders/orderForm/orderFormTotals';
import { formatSalesPersonOptionLabel } from '../orders/orderForm/orderFormSalesPerson';
import {
  buildQuotePayload,
  resolveCreatorSalesPersonString,
  toQuoteUpdate,
  validateOrderFormForSave,
} from './quotationFormPayload';
import styles from './Quotations.module.scss';

interface QuotationFormProps {
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
  organizationId: string;
  addToast: (message: string) => void;
  quoteToEdit?: Quote | null;
}

const ProductThumb: React.FC<{
  imageUrl?: string;
  alt: string;
  size?: 'sm' | 'md';
}> = ({ imageUrl, alt, size = 'md' }) => {
  const [failed, setFailed] = useState(false);
  const className =
    size === 'sm' ? styles.productThumbSm : styles.productThumb;
  const placeholderClass =
    size === 'sm'
      ? styles.productThumbPlaceholderSm
      : styles.productThumbPlaceholder;

  if (!imageUrl?.trim() || failed) {
    return (
      <div className={placeholderClass} title="Sin imagen" aria-hidden>
        <FiImage size={size === 'sm' ? 14 : 18} />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
    />
  );
};

const QuotationForm: React.FC<QuotationFormProps> = ({
  show,
  onClose,
  onSaved,
  organizationId,
  addToast,
  quoteToEdit = null,
}) => {
  const { showLoading, hideLoading } = useLoading();
  const isEditing = Boolean(quoteToEdit?.id);

  const [clients, setClients] = useState<Client[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientPending, setClientPending] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const [salesPeople, setSalesPeople] = useState<SalesPerson[]>([]);
  const [vendorPending, setVendorPending] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string>('');

  const [products, setProducts] = useState<Product[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [productPending, setProductPending] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<OrderFormLine[]>([]);
  const [showProductPicker, setShowProductPicker] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState('');
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<
    Array<{ id: number; name: string; enabled: boolean }>
  >([]);
  const [paymentCurrency, setPaymentCurrency] = useState('');
  const [currency, setCurrency] = useState('VES');
  const [referenceCurrency, setReferenceCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(36);
  const [defaultExchangeRate, setDefaultExchangeRate] = useState(36);
  const [defaultExchangeSourceId, setDefaultExchangeSourceId] = useState('');
  const [exchangeSources, setExchangeSources] = useState<ExchangeSourceOption[]>(
    []
  );
  const [exchangeRateMode, setExchangeRateMode] = useState<
    'org' | 'source' | 'manual'
  >('org');
  const [exchangeSourceOverrideId, setExchangeSourceOverrideId] = useState('');
  const [applyIva, setApplyIva] = useState(true);
  const [taxConfig, setTaxConfig] = useState<OrderFormTaxConfig>({
    iva: 16,
    primaryTaxId: '',
    taxes: [],
  });
  const [shippingAddress, setShippingAddress] = useState('');
  const [comment, setComment] = useState('');
  const [creditEnabled, setCreditEnabled] = useState(false);
  const [creditTermsDays, setCreditTermsDays] = useState('');
  const [creditDueDate, setCreditDueDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  const totals = useMemo(
    () =>
      computeOrderTotals(selectedProducts, {
        applyIva,
        taxConfig,
        orderDiscountPercent: 0,
      }),
    [selectedProducts, applyIva, taxConfig]
  );

  const showLocalEquivalent =
    Boolean(currency) &&
    Boolean(referenceCurrency) &&
    currency.toUpperCase() !== referenceCurrency.toUpperCase();

  const getCurrencySymbol = useCallback((code?: string): string => {
    if (!code) return '';
    return SUPPORTED_CURRENCIES.find(c => c.code === code)?.symbol || code;
  }, []);

  const money = useCallback(
    (amount: number, code: string = referenceCurrency) => {
      const rounded = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(amount) || 0);
      const symbol = getCurrencySymbol(code);
      return symbol ? `${symbol}${rounded}` : rounded;
    },
    [referenceCurrency, getCurrencySymbol]
  );

  const lineUnitPrice = (line: OrderFormLine) =>
    line.customPrice !== undefined ? Number(line.customPrice) : Number(line.price) || 0;

  const lineSubtotal = (line: OrderFormLine) => {
    const price = lineUnitPrice(line);
    const qty = Number(line.selectedQuantity) || 0;
    const discount = Number(line.discount) || 0;
    return price * qty * (1 - discount / 100);
  };

  const resetForm = useCallback(() => {
    setSelectedClient(null);
    setClientSearch('');
    setSelectedVendorId('');
    setSelectedProducts([]);
    setProductSearch('');
    setShowProductPicker(false);
    setPaymentMethod('');
    setPaymentCurrency('');
    setShippingAddress('');
    setComment('');
    setCreditEnabled(false);
    setCreditTermsDays('');
    setCreditDueDate('');
    setApplyIva(true);
    setExchangeRateMode('org');
    setExchangeSourceOverrideId('');
    setFormError(null);
    setHydrated(false);
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const config = await loadConfigurationFactors(organizationId);
      const paymentMethods = config.paymentMethodsAvailable || [];
      setAvailablePaymentMethods(paymentMethods);
      const firstEnabled = paymentMethods.find(pm => pm.enabled);
      if (firstEnabled && !quoteToEdit) {
        setPaymentMethod(firstEnabled.name);
      }
      setCurrency(config.currency || 'VES');
      setReferenceCurrency(config.referenceCurrency || 'USD');
      if (!quoteToEdit) {
        setPaymentCurrency(config.referenceCurrency || 'USD');
      }
      const rate = Number(config.exchangeRate) || 36;
      setDefaultExchangeRate(rate);
      if (!quoteToEdit) {
        setExchangeRate(rate);
        setExchangeRateMode('org');
        setExchangeSourceOverrideId('');
      }
      setDefaultExchangeSourceId(
        config.useAutomaticExchangeRate ? config.exchangeSourceId || '' : ''
      );
      const taxes = (config.taxes || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        rate: Number(t.rate) || 0,
        enabled: t.enabled !== false,
      }));
      setTaxConfig({
        iva: Number(config.iva) || 16,
        primaryTaxId: config.primaryTaxId || '',
        taxes,
      });
    } catch (e) {
      console.error('Failed to load configuration for quote form', e);
    }
  }, [organizationId, quoteToEdit]);

  const loadVendors = useCallback(async () => {
    setVendorPending(true);
    try {
      const people = await searchSalesPeople(organizationId, '');
      setSalesPeople(people || []);
      const user = await getCurrentAuthUser();
      if (!quoteToEdit && user?.email) {
        const match = (people || []).find(
          p => p.email?.toLowerCase() === user.email?.toLowerCase()
        );
        if (match?.id) {
          setSelectedVendorId(match.id);
        } else {
          setSelectedVendorId('admin');
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setVendorPending(false);
    }
  }, [organizationId, quoteToEdit]);

  useEffect(() => {
    if (!show) return;
    resetForm();
    void loadConfig();
    void loadVendors();
  }, [show, resetForm, loadConfig, loadVendors]);

  useEffect(() => {
    if (!show || !quoteToEdit || hydrated) return;
    const hydrate = async () => {
      try {
        setSelectedClient({
          id: quoteToEdit.clientId,
          code: '',
          rif: '',
          businessName: quoteToEdit.clientName,
        } as Client);
        setShippingAddress(quoteToEdit.shippingAddress || '');
        setComment(quoteToEdit.comment || '');
        setPaymentMethod(quoteToEdit.paymentMethod || '');
        setPaymentCurrency(
          quoteToEdit.paymentCurrency || referenceCurrency || 'USD'
        );
        if (typeof quoteToEdit.exchangeRate === 'number') {
          setExchangeRate(quoteToEdit.exchangeRate);
          setExchangeRateMode('manual');
          setExchangeSourceOverrideId('');
        }
        const hasIva = Number(quoteToEdit.ivaAmount || 0) > 0;
        setApplyIva(hasIva);
        if (quoteToEdit.creditTermsDays && quoteToEdit.creditTermsDays > 0) {
          setCreditEnabled(true);
          setCreditTermsDays(String(quoteToEdit.creditTermsDays));
        }
        if (quoteToEdit.dueDate) {
          const d =
            quoteToEdit.dueDate instanceof Date
              ? quoteToEdit.dueDate
              : new Date(quoteToEdit.dueDate as any);
          if (!Number.isNaN(d.getTime())) {
            setCreditDueDate(d.toISOString().slice(0, 10));
          }
        }
        setSelectedProducts(
          (quoteToEdit.products || []).map(p => ({
            id: p.productId,
            productId: p.productId,
            code: p.code,
            name: p.name,
            brand: p.brand,
            description: p.description,
            price: p.price,
            cost: p.cost,
            selectedQuantity: p.quantity,
            discount: p.discount ?? 0,
            taxId: p.taxId,
            imageUrl: p.imageUrl,
            warehouseId: p.warehouseId,
          }))
        );
        const creator = (quoteToEdit.creatorSalesPerson || '').trim();
        if (creator) {
          const match = salesPeople.find(
            sp =>
              sp.email?.toLowerCase() === creator.toLowerCase() ||
              sp.id === creator
          );
          setSelectedVendorId(match?.id || 'admin');
        }
        setHydrated(true);
      } catch (e) {
        console.error(e);
        setHydrated(true);
      }
    };
    void hydrate();
  }, [
    show,
    quoteToEdit,
    hydrated,
    salesPeople,
    referenceCurrency,
  ]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    const run = async () => {
      setClientPending(true);
      try {
        const result = await loadClients(organizationId, clientSearch, 15);
        if (!cancelled) setClients(result.clients || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setClientPending(false);
      }
    };
    const t = window.setTimeout(run, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [show, organizationId, clientSearch]);

  useEffect(() => {
    if (!show || !referenceCurrency || !currency) {
      setExchangeSources([]);
      return;
    }
    let cancelled = false;
    const loadSources = async () => {
      try {
        const sources = await listExchangeSourcesForPair({
          fromCurrency: referenceCurrency,
          toCurrency: currency,
        });
        if (cancelled) return;
        setExchangeSources(
          sources.map(s => ({
            id: s.id,
            name: s.name,
            rate: s.rate,
            sourceUrl: s.sourceUrl,
            lastFetchedAt: s.lastFetchedAt,
          }))
        );
      } catch (e) {
        console.warn('Failed to load exchange sources', e);
        if (!cancelled) setExchangeSources([]);
      }
    };
    void loadSources();
    return () => {
      cancelled = true;
    };
  }, [show, referenceCurrency, currency]);

  useEffect(() => {
    if (exchangeRateMode !== 'org') return;
    setExchangeRate(defaultExchangeRate);
  }, [exchangeRateMode, defaultExchangeRate]);

  useEffect(() => {
    if (!show || !showProductPicker) return;
    let cancelled = false;
    const run = async () => {
      setProductPending(true);
      try {
        const result = await loadProducts(
          organizationId,
          productSearch,
          undefined,
          30
        );
        if (!cancelled) setProducts(result.products || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setProductPending(false);
      }
    };
    const t = window.setTimeout(run, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [show, showProductPicker, organizationId, productSearch]);

  const addProduct = (product: Product) => {
    const id = product.id!;
    setSelectedProducts(prev => {
      const existing = prev.find(p => (p.productId || p.id) === id);
      if (existing) {
        return prev.map(p =>
          (p.productId || p.id) === id
            ? {
                ...p,
                selectedQuantity: Number(p.selectedQuantity || 0) + 1,
              }
            : p
        );
      }
      return [
        ...prev,
        {
          id,
          productId: id,
          code: product.code || '',
          name: product.name || '',
          brand: product.brand,
          description: product.description,
          price: Number(product.price) || 0,
          cost: product.cost != null ? Number(product.cost) : undefined,
          selectedQuantity: 1,
          discount: 0,
          taxId: product.taxId,
          imageUrl: product.imageUrl,
        },
      ];
    });
    setShowProductPicker(false);
    setProductSearch('');
  };

  const updateLine = (productId: string, patch: Partial<OrderFormLine>) => {
    setSelectedProducts(prev =>
      prev.map(p => ((p.productId || p.id) === productId ? { ...p, ...patch } : p))
    );
  };

  const removeLine = (productId: string) => {
    setSelectedProducts(prev =>
      prev.filter(p => (p.productId || p.id) !== productId)
    );
  };

  const handleSave = async () => {
    setFormError(null);
    const validation = validateOrderFormForSave({
      selectedClient,
      selectedProducts,
      selectedVendorId,
    });
    if (!validation.valid) {
      setFormError(
        validation.message ||
          'Selecciona un cliente, un vendedor y al menos un producto.'
      );
      return;
    }
    if (!paymentMethod) {
      setFormError('Selecciona un método de pago.');
      return;
    }

    showLoading('Guardando cotización');
    try {
      const user = await getCurrentAuthUser();
      const creatorSalesPerson = resolveCreatorSalesPersonString({
        selectedVendorId,
        salesPeople,
        currentUserEmail: user?.email,
      });
      const payload = buildQuotePayload({
        selectedClient: selectedClient!,
        shippingAddress,
        creatorSalesPerson,
        paymentMethod,
        paymentCurrency: paymentCurrency || referenceCurrency,
        creditEnabled,
        creditTermsDays,
        creditDueDate,
        totals,
        exchangeRate,
        comment,
        selectedProducts,
        applyIva,
        taxConfig,
      });

      if (isEditing && quoteToEdit?.id) {
        await updateQuoteServer(
          organizationId,
          quoteToEdit.id,
          toQuoteUpdate(payload)
        );
        addToast('Cotización actualizada');
      } else {
        await createQuoteServer(organizationId, payload);
        addToast('Cotización creada');
      }
      onSaved();
      onClose();
    } catch (error: any) {
      console.error(error);
      setFormError(
        error?.message || 'No se pudo guardar la cotización'
      );
    } finally {
      hideLoading();
    }
  };

  const handleClose = useCallback(() => {
    setShowProductPicker(false);
    onClose();
  }, [onClose]);

  return (
    <>
      <Offcanvas
        show={show}
        onHide={handleClose}
        placement="end"
        scroll={false}
        backdrop
        className={styles.drawer}
        backdropClassName={styles.drawerBackdrop}
      >
        <div className={styles.drawerForm}>
          <Offcanvas.Header className={styles.header}>
            <div className={styles.headerRow}>
              <Offcanvas.Title className={styles.headerTitle}>
                {isEditing ? 'Editar cotización' : 'Nueva cotización'}
              </Offcanvas.Title>
              <button
                type="button"
                className={styles.headerClose}
                onClick={handleClose}
                aria-label="Cerrar"
              >
                <FiX size={22} aria-hidden />
              </button>
            </div>
          </Offcanvas.Header>

          <Offcanvas.Body className={styles.body}>
            {formError && (
              <Alert variant="danger" className="mb-3">
                {formError}
              </Alert>
            )}

            <section className={styles.formSection}>
              <div className={styles.formSectionTitle}>Cliente</div>
              <Form.Group className="mb-2">
                <Form.Label>Buscar cliente</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FiSearch />
                  </InputGroup.Text>
                  <FormControl
                    value={clientSearch}
                    onChange={e => setClientSearch(e.target.value)}
                    placeholder="Nombre o código"
                  />
                </InputGroup>
              </Form.Group>
              <Form.Select
                value={selectedClient?.id || ''}
                onChange={e => {
                  const client =
                    clients.find(c => c.id === e.target.value) || null;
                  setSelectedClient(client);
                  if (client?.address && !shippingAddress) {
                    setShippingAddress(client.address);
                  }
                  if (client?.creditTerms && client.creditTerms > 0) {
                    setCreditEnabled(true);
                    setCreditTermsDays(String(client.creditTerms));
                  }
                }}
                disabled={clientPending}
              >
                <option value="">
                  {clientPending ? 'Cargando…' : 'Seleccionar cliente'}
                </option>
                {selectedClient &&
                  !clients.some(c => c.id === selectedClient.id) && (
                    <option value={selectedClient.id}>
                      {selectedClient.businessName || selectedClient.code}
                    </option>
                  )}
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.businessName || c.code}
                  </option>
                ))}
              </Form.Select>
            </section>

            <section className={styles.formSection}>
              <div className={styles.formSectionTitle}>Vendedor</div>
              <Form.Select
                value={selectedVendorId}
                onChange={e => setSelectedVendorId(e.target.value)}
                disabled={vendorPending}
              >
                <option value="">
                  {vendorPending ? 'Cargando…' : 'Seleccionar vendedor'}
                </option>
                <option value="admin">Administrador (yo)</option>
                {salesPeople.map(sp => (
                  <option key={sp.id} value={sp.id}>
                    {formatSalesPersonOptionLabel(sp)}
                  </option>
                ))}
              </Form.Select>
            </section>

            <section className={styles.formSection}>
              <div className={styles.formSectionTitle}>Detalle comercial</div>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Método de pago</Form.Label>
                    <Form.Select
                      value={paymentMethod}
                      onChange={e => setPaymentMethod(e.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {availablePaymentMethods
                        .filter(pm => pm.enabled)
                        .map(pm => (
                          <option key={pm.id} value={pm.name}>
                            {pm.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Moneda de pago</Form.Label>
                    <Form.Select
                      value={paymentCurrency}
                      onChange={e => setPaymentCurrency(e.target.value)}
                    >
                      <option value={referenceCurrency}>
                        {referenceCurrency} (referencia)
                      </option>
                      <option value={currency}>{currency} (local)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>
                      Tipo de cambio ({referenceCurrency}→{currency})
                    </Form.Label>
                    <div className="d-flex flex-column flex-md-row gap-2 mb-2">
                      <Form.Select
                        value={exchangeRateMode}
                        onChange={e => {
                          const mode = e.currentTarget.value as
                            | 'org'
                            | 'source'
                            | 'manual';
                          setExchangeRateMode(mode);
                          if (mode === 'org') {
                            setExchangeSourceOverrideId('');
                            setExchangeRate(defaultExchangeRate);
                          } else if (mode === 'manual') {
                            setExchangeSourceOverrideId('');
                          }
                        }}
                      >
                        <option value="org">
                          Automática de la organización (
                          {formatExchangeRate(defaultExchangeRate)})
                        </option>
                        <option value="source">Desde una fuente</option>
                        <option value="manual">Manual</option>
                      </Form.Select>
                      <div className="text-muted small align-self-center text-nowrap">
                        Tasa activa:{' '}
                        <strong>{formatExchangeRate(exchangeRate)}:1</strong>
                        {exchangeSourceOverrideId
                          ? ' (fuente)'
                          : exchangeRateMode === 'org' && defaultExchangeSourceId
                            ? ' (org)'
                            : exchangeRateMode === 'manual'
                              ? ' (manual)'
                              : ''}
                      </div>
                    </div>
                    {exchangeRateMode === 'source' && (
                      <Form.Select
                        className="mb-2"
                        value={exchangeSourceOverrideId}
                        onChange={e => {
                          const selectedId = e.currentTarget.value;
                          setExchangeSourceOverrideId(selectedId);
                          if (!selectedId) return;
                          const src =
                            exchangeSources.find(s => s.id === selectedId) ??
                            null;
                          if (src && typeof src.rate === 'number') {
                            setExchangeRate(src.rate);
                          }
                        }}
                      >
                        <option value="">Seleccione una fuente</option>
                        {exchangeSources.map(src => (
                          <option key={src.id} value={src.id}>
                            {(src.name && src.name.trim().length > 0
                              ? src.name
                              : src.sourceUrl || src.id) +
                              (typeof src.rate === 'number'
                                ? ` (${formatExchangeRate(src.rate)})`
                                : '')}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                    {exchangeRateMode === 'manual' && (
                      <FormControl
                        type="number"
                        min={0}
                        step="any"
                        value={exchangeRate}
                        onChange={e => {
                          const value = Number(e.currentTarget.value);
                          if (!Number.isFinite(value) || value <= 0) return;
                          setExchangeRate(value);
                        }}
                      />
                    )}
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Check
                    type="switch"
                    id="quote-apply-iva"
                    label="Aplicar impuestos (IVA)"
                    checked={applyIva}
                    onChange={e => setApplyIva(e.target.checked)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="quote-credit"
                    label="Crédito / plazo"
                    checked={creditEnabled}
                    onChange={e => setCreditEnabled(e.target.checked)}
                  />
                </Col>
                {creditEnabled && (
                  <>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Días de crédito</Form.Label>
                        <FormControl
                          type="number"
                          min={1}
                          value={creditTermsDays}
                          onChange={e => setCreditTermsDays(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Vence</Form.Label>
                        <FormControl
                          type="date"
                          value={creditDueDate}
                          onChange={e => setCreditDueDate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Dirección de envío</Form.Label>
                    <FormControl
                      value={shippingAddress}
                      onChange={e => setShippingAddress(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Comentario</Form.Label>
                    <FormControl
                      as="textarea"
                      rows={2}
                      value={comment}
                      onChange={e => setComment(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <section className={styles.formSection}>
              <div className={styles.productsHeader}>
                <div className={styles.formSectionTitle}>Productos</div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    setProductSearch('');
                    setShowProductPicker(true);
                  }}
                >
                  <FiPlus className="me-1" />
                  Agregar
                </Button>
              </div>

              {selectedProducts.length === 0 ? (
                <Alert variant="light" className="mb-0">
                  Agrega productos para armar la cotización.
                </Alert>
              ) : (
                <div className={styles.productLines}>
                  {selectedProducts.map(line => {
                    const pid = String(line.productId || line.id);
                    const unit = lineUnitPrice(line);
                    return (
                      <div key={pid} className={styles.productLine}>
                        <div className={styles.productLineMain}>
                          <ProductThumb
                            imageUrl={line.imageUrl}
                            alt={line.name || line.code || 'Producto'}
                          />
                          <div className={styles.productLineInfo}>
                            <div className={styles.primaryLine}>
                              {line.code}
                            </div>
                            <div className={styles.productLineName}>
                              {line.name}
                            </div>
                            <div className={styles.secondaryLine}>
                              Importe: {money(lineSubtotal(line))}
                            </div>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className={styles.productLineRemove}
                            onClick={() => removeLine(pid)}
                            aria-label="Quitar producto"
                          >
                            <FiTrash2 />
                          </Button>
                        </div>
                        <div className={styles.productLineFields}>
                          <Form.Group className={styles.productLineField}>
                            <Form.Label>Cant.</Form.Label>
                            <FormControl
                              type="number"
                              min={0.001}
                              step="any"
                              value={line.selectedQuantity}
                              onChange={e =>
                                updateLine(pid, {
                                  selectedQuantity:
                                    Number(e.target.value) || 0,
                                })
                              }
                            />
                          </Form.Group>
                          <Form.Group className={styles.productLineField}>
                            <Form.Label>
                              Precio ({referenceCurrency})
                            </Form.Label>
                            <FormControl
                              type="number"
                              min={0}
                              step="0.01"
                              value={Number.isFinite(unit) ? unit : 0}
                              onChange={e =>
                                updateLine(pid, {
                                  customPrice: Number(e.target.value) || 0,
                                })
                              }
                            />
                          </Form.Group>
                          <Form.Group className={styles.productLineField}>
                            <Form.Label>Desc. %</Form.Label>
                            <FormControl
                              type="number"
                              min={0}
                              max={100}
                              step="any"
                              value={line.discount}
                              onChange={e =>
                                updateLine(pid, {
                                  discount: Number(e.target.value) || 0,
                                })
                              }
                            />
                          </Form.Group>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </Offcanvas.Body>

          <div className={styles.footer}>
            <div className={styles.totalsMeta}>
              {referenceCurrency}→{currency} · Tasa:{' '}
              {formatExchangeRate(exchangeRate)}:1
              {selectedProducts.length > 0
                ? ` · ${selectedProducts.length} producto${
                    selectedProducts.length === 1 ? '' : 's'
                  }`
                : ''}
            </div>
            <div className={styles.totalsBar}>
              <div className={styles.totalsRow}>
                <span>Subtotal</span>
                <span className={styles.totalsStack}>
                  <span>{money(totals.subtotal, referenceCurrency)}</span>
                  {showLocalEquivalent ? (
                    <span className={styles.totalsSecondary}>
                      {money(totals.subtotal * exchangeRate, currency)}
                    </span>
                  ) : null}
                </span>
              </div>
              <div className={styles.totalsRow}>
                <span>IVA</span>
                <span className={styles.totalsStack}>
                  <span>{money(totals.ivaAmount, referenceCurrency)}</span>
                  {showLocalEquivalent ? (
                    <span className={styles.totalsSecondary}>
                      {money(totals.ivaAmount * exchangeRate, currency)}
                    </span>
                  ) : null}
                </span>
              </div>
              <div className={`${styles.totalsRow} ${styles.totalsRowTotal}`}>
                <span>Total</span>
                <span className={styles.totalsStack}>
                  <span>{money(totals.total, referenceCurrency)}</span>
                  {showLocalEquivalent ? (
                    <span className={styles.totalsSecondary}>
                      {money(totals.total * exchangeRate, currency)}
                    </span>
                  ) : null}
                </span>
              </div>
            </div>
            <div className={styles.footerActions}>
              <Button variant="outline-secondary" onClick={handleClose}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={() => void handleSave()}>
                Guardar cotización
              </Button>
            </div>
          </div>
        </div>
      </Offcanvas>

      <Offcanvas
        show={show && showProductPicker}
        onHide={() => setShowProductPicker(false)}
        placement="end"
        scroll={false}
        backdrop
        className={styles.pickerDrawer}
        backdropClassName={styles.pickerDrawerBackdrop}
      >
        <Offcanvas.Header className={styles.header}>
          <div className={styles.headerRow}>
            <Offcanvas.Title className={styles.headerTitle}>
              Agregar producto
            </Offcanvas.Title>
            <button
              type="button"
              className={styles.headerClose}
              onClick={() => setShowProductPicker(false)}
              aria-label="Cerrar"
            >
              <FiX size={22} aria-hidden />
            </button>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body className={styles.pickerBody}>
          <Form.Group className="mb-3">
            <InputGroup>
              <InputGroup.Text>
                <FiSearch />
              </InputGroup.Text>
              <FormControl
                autoFocus
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                placeholder="Buscar por nombre o código"
              />
              {productPending && (
                <InputGroup.Text>
                  <Spinner animation="border" size="sm" />
                </InputGroup.Text>
              )}
            </InputGroup>
          </Form.Group>

          {productPending && products.length === 0 ? (
            <div className={styles.productSearchEmpty}>
              <Spinner animation="border" size="sm" className="me-2" />
              Buscando productos…
            </div>
          ) : products.length === 0 ? (
            <div className={styles.productSearchEmpty}>
              {productSearch.trim()
                ? 'No se encontraron productos.'
                : 'Escribe para buscar productos.'}
            </div>
          ) : (
            <div className={styles.productSearchResults}>
              {products.map(p => (
                <div
                  key={p.id}
                  className={styles.productSearchItem}
                  onClick={() => addProduct(p)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      addProduct(p);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <ProductThumb
                    imageUrl={p.imageUrl}
                    alt={p.name || p.code || 'Producto'}
                  />
                  <div className={styles.productSearchMeta}>
                    <div className={styles.primaryLine}>
                      {p.code} — {p.name}
                    </div>
                    {p.brand ? (
                      <div className={styles.secondaryLine}>{p.brand}</div>
                    ) : null}
                  </div>
                  <div className={styles.productSearchPrice}>
                    {money(Number(p.price) || 0)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default QuotationForm;
